import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import { adminStore, fetchOrgs, type Org } from "../stores/admin";
import { tx, transact, id } from "@/lib/instantdb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AlertCircle, Plus, Pencil, Trash2, Building2 } from "lucide-react";

interface OrgFormData {
  name: string;
  slug: string;
}

const defaultFormData: OrgFormData = {
  name: "",
  slug: "",
};

function OrgDialog({
  open,
  onOpenChange,
  org,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org: Org | null;
  onSubmit: (data: OrgFormData) => Promise<void>;
}) {
  const [formData, setFormData] = useState<OrgFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (org) {
      setFormData({
        name: org.name,
        slug: org.slug,
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [org, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const isEdit = !!org;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Organization" : "Create Organization"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update the configuration for ${org.name}`
              : "Create a new organization"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Organization Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                const name = e.target.value;
                setFormData({
                  ...formData,
                  name,
                  slug: isEdit ? formData.slug : generateSlug(name),
                });
              }}
              placeholder="Acme Corporation"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) =>
                setFormData({ ...formData, slug: e.target.value })
              }
              placeholder="acme-corp"
              disabled={isEdit}
              required
            />
            <p className="text-xs text-muted-foreground">
              URL-friendly identifier (lowercase, no spaces)
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}{" "}
              Organization
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Organizations() {
  const { orgs, orgsLoading, error } = useSnapshot(adminStore);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Org | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<Org | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchOrgs();
  }, []);

  const handleCreateOrg = async (data: OrgFormData) => {
    try {
      const orgId = id();
      await transact(
        tx.admin_orgs[orgId].update({
          name: data.name,
          slug: data.slug,
          createdAt: Date.now(),
        }),
      );
      toast.success("Organization created successfully");
      await fetchOrgs(true);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to create organization",
      );
      throw e;
    }
  };

  const handleUpdateOrg = async (data: OrgFormData) => {
    if (!editingOrg) return;
    try {
      await transact(
        tx.admin_orgs[editingOrg.id].update({
          name: data.name,
        }),
      );
      toast.success("Organization updated successfully");
      await fetchOrgs(true);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to update organization",
      );
      throw e;
    }
  };

  const handleDeleteOrg = async () => {
    if (!orgToDelete) return;
    setDeleteLoading(true);
    try {
      await transact(tx.admin_orgs[orgToDelete.id].delete());
      toast.success("Organization deleted successfully");
      setDeleteDialogOpen(false);
      setOrgToDelete(null);
      await fetchOrgs(true);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to delete organization",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditDialog = (org: Org) => {
    setEditingOrg(org);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingOrg(null);
    setDialogOpen(true);
  };

  const openDeleteDialog = (org: Org) => {
    setOrgToDelete(org);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Organizations</h1>
            <p className="mt-2 text-muted-foreground">
              Manage organizations and their members
            </p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Create Organization
          </Button>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Organizations Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                All Organizations ({orgs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orgsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgs.length > 0 ? (
                      orgs.map((org) => (
                        <TableRow key={org.id}>
                          <TableCell className="font-medium">
                            {org.name}
                          </TableCell>
                          <TableCell>
                            <code className="text-sm bg-muted px-2 py-1 rounded">
                              {org.slug}
                            </code>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(org.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(org as Org)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => openDeleteDialog(org as Org)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-muted-foreground py-8"
                        >
                          No organizations found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Org Dialog */}
      <OrgDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingOrg(null);
        }}
        org={editingOrg}
        onSubmit={editingOrg ? handleUpdateOrg : handleCreateOrg}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Organization"
        description={`Are you sure you want to delete "${orgToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteOrg}
        loading={deleteLoading}
      />
    </div>
  );
}
